import { useEffect, useState } from "react";

import type { DateValueType } from "../../../DatepickerV2";
import type { DropdownOption } from "../../../DropDownV2";
import type { MultiSelectOption } from "../../../MultiSelect";
import type { SegmentOption } from "../../../SegmentControl";
import type { ClientTypeFilterValue } from "../../ClientTypeFilter";
import type { ReturnedTranslatedContentClientsPageHook } from "../../types";

import { Button as ButtonV2 } from "../../../Button";
import { Datepicker } from "../../../Datepicker";
import { MultiSelect } from "../../../MultiSelect";
import { ResponsiveModal } from "../../../ResponsiveModal";
import { SegmentControl } from "../../../SegmentControl";
import {
  ALL_CLIENT_TYPES,
  ClientTypeFilterFields,
  includesSharedClients,
} from "../../ClientTypeFilter";
import { FilterField } from "../../Common/FilterField";
import { CLIENT_TRADING_TABLE_FILTER_REPORT_START_DATE } from "../consts";

const DEFAULT_DATE_RANGE_DAYS = 30;

const NO_SUB_IBS: MultiSelectOption[] = [];

const defaultDateRange = (): DateValueType => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - DEFAULT_DATE_RANGE_DAYS);
  return { startDate, endDate };
};

type FilterDimension = "rebateProgram" | "rebateAccount";

// Neither dimension has a default selection -- rebate account only becomes
// "the active dimension" once the user has actually picked one, symmetric
// with rebate program's empty-is-off state.
const deriveInitialDimension = (
  selectedRebateAccounts: DropdownOption[],
): FilterDimension =>
  selectedRebateAccounts.length > 0 ? "rebateAccount" : "rebateProgram";

export type ClientTradingFilterModalProps = {
  isOpen: boolean;
  onClose: () => void;
  rebateAccountOptions: DropdownOption[];
  selectedRebateAccounts: DropdownOption[];
  onRebateAccountsChange: (options: DropdownOption[]) => void;
  rebateProgramOptions: MultiSelectOption[];
  selectedRebatePrograms: MultiSelectOption[];
  onRebateProgramsChange: (options: MultiSelectOption[]) => void;
  translations: ReturnedTranslatedContentClientsPageHook;
  /** Gated by the part-3795-materialized-view-hoid flag. */
  showRebateProgramFilter?: boolean;
  /** Date range field, shown inside the modal only when both `dateRange` and
   * `onDateRangeChange` are supplied -- the part-4039-master-sub-ib-phase-2
   * flag's "off" state (the toolbar's own date field, "on", passes neither). */
  dateRange?: DateValueType;
  onDateRangeChange?: (value: DateValueType) => void;
  onDateTooltipHover?: (isMobile: boolean) => void;
  showClientTypeFilter?: boolean;
  selectedClientTypes?: ClientTypeFilterValue[];
  onClientTypesChange?: (clientTypes: ClientTypeFilterValue[]) => void;
  subIBOptions?: MultiSelectOption[];
  selectedSubIBs?: MultiSelectOption[];
  onSubIBsChange?: (subIBs: MultiSelectOption[]) => void;
  subIBsLoadFailed?: boolean;
  onRetrySubIBs?: () => void;
};

// Fields are edited as a local draft and only committed to the real report
// context (which triggers a refetch) when the user clicks Apply -- Clear only
// resets the draft, it doesn't filter anything until Apply is pressed too.
export const ClientTradingFilterModal = ({
  isOpen,
  onClose,
  rebateAccountOptions,
  selectedRebateAccounts,
  onRebateAccountsChange,
  rebateProgramOptions,
  selectedRebatePrograms,
  onRebateProgramsChange,
  translations: { translatedContent, language },
  showRebateProgramFilter,
  dateRange,
  onDateRangeChange,
  onDateTooltipHover,
  showClientTypeFilter = false,
  selectedClientTypes = ALL_CLIENT_TYPES,
  onClientTypesChange,
  subIBOptions = NO_SUB_IBS,
  selectedSubIBs = NO_SUB_IBS,
  onSubIBsChange,
  subIBsLoadFailed,
  onRetrySubIBs,
}: ClientTradingFilterModalProps) => {
  const showDateField = dateRange != null && onDateRangeChange != null;

  const [draftRebateAccounts, setDraftRebateAccounts] = useState(
    selectedRebateAccounts,
  );
  const [draftRebatePrograms, setDraftRebatePrograms] = useState(
    selectedRebatePrograms,
  );
  const [draftDateRange, setDraftDateRange] = useState(
    dateRange ?? defaultDateRange(),
  );
  const [dimension, setDimension] = useState<FilterDimension>(() =>
    deriveInitialDimension(selectedRebateAccounts),
  );
  const [draftClientTypes, setDraftClientTypes] = useState(selectedClientTypes);
  const [draftSubIBs, setDraftSubIBs] = useState(selectedSubIBs);

  // Re-seed the draft from the live filters whenever the modal (re)opens.
  useEffect(() => {
    if (!isOpen) return;
    setDraftRebateAccounts(selectedRebateAccounts);
    setDraftRebatePrograms(selectedRebatePrograms);
    if (dateRange) setDraftDateRange(dateRange);
    setDimension(deriveInitialDimension(selectedRebateAccounts));
    setDraftClientTypes(selectedClientTypes);
    setDraftSubIBs(selectedSubIBs);
  }, [
    isOpen,
    selectedRebateAccounts,
    selectedRebatePrograms,
    dateRange,
    selectedClientTypes,
    selectedSubIBs,
  ]);

  // Rebate program and rebate account are an either/or -- switching clears
  // the other dimension's draft back to its neutral/off state so only one of
  // the pair is ever committed on Apply.
  const switchDimension = (next: FilterDimension) => {
    if (next === dimension) return;
    if (next === "rebateProgram") {
      setDraftRebateAccounts([]);
    } else {
      setDraftRebatePrograms([]);
    }
    setDimension(next);
  };

  const handleClear = () => {
    setDraftRebateAccounts([]);
    setDraftRebatePrograms([]);
    setDraftDateRange(defaultDateRange());
    setDraftClientTypes(ALL_CLIENT_TYPES);
    setDraftSubIBs(NO_SUB_IBS);
  };

  const handleApply = () => {
    onRebateAccountsChange(draftRebateAccounts);
    onRebateProgramsChange(draftRebatePrograms);
    if (showDateField) onDateRangeChange?.(draftDateRange);
    if (showClientTypeFilter) {
      onClientTypesChange?.(draftClientTypes);
      onSubIBsChange?.(
        includesSharedClients(draftClientTypes) ? draftSubIBs : NO_SUB_IBS,
      );
    }
    onClose();
  };

  const rebateProgramField = (
    <FilterField
      label={translatedContent.filters?.rebatePrograms}
      hint={translatedContent.filters?.rebateProgramsHint}
    >
      {(controlProps) => (
        <MultiSelect
          modal
          id="rebateProgramFilterModal"
          options={rebateProgramOptions}
          selected={draftRebatePrograms}
          onChange={setDraftRebatePrograms}
          placeholder={translatedContent.filters?.rebateProgramsPlaceholder}
          data-testid="clients_filter_modal_rebate_program_select"
          {...controlProps}
        />
      )}
    </FilterField>
  );

  const rebateAccountField = (
    <FilterField
      label={translatedContent.filters?.rebateAccounts}
      hint={translatedContent.filters?.rebateAccountsHint}
    >
      {(controlProps) => (
        <MultiSelect
          modal
          id="rebateAccountFilterModal"
          options={rebateAccountOptions}
          selected={draftRebateAccounts}
          onChange={setDraftRebateAccounts}
          placeholder={translatedContent.filters?.rebateAccountsPlaceholder}
          data-testid="clients_filter_modal_rebate_account_select"
          {...controlProps}
        />
      )}
    </FilterField>
  );

  const dimensionOptions: SegmentOption<FilterDimension>[] = [
    {
      label: translatedContent.filters?.rebatePrograms ?? "",
      value: "rebateProgram",
    },
    {
      label: translatedContent.filters?.rebateAccounts ?? "",
      value: "rebateAccount",
    },
  ];

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      isMobile={false}
      title={translatedContent.filters?.filterButton ?? "Filter"}
      contentOverflow="visible"
      footer={
        <div className="flex justify-end gap-3">
          <ButtonV2 variant="outline" size="md" onClick={handleClear}>
            {translatedContent.filters?.clear ?? "Clear"}
          </ButtonV2>
          <ButtonV2 variant="brand" size="md" onClick={handleApply}>
            {translatedContent.apply}
          </ButtonV2>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {showRebateProgramFilter ? (
          <>
            <SegmentControl
              fullWidth
              options={dimensionOptions}
              value={dimension}
              onChange={switchDimension}
            />
            {dimension === "rebateProgram"
              ? rebateProgramField
              : rebateAccountField}
          </>
        ) : (
          rebateAccountField
        )}

        {showClientTypeFilter && (
          <ClientTypeFilterFields
            clientTypes={draftClientTypes}
            onClientTypesChange={setDraftClientTypes}
            subIBOptions={subIBOptions}
            selectedSubIBs={draftSubIBs}
            onSubIBsChange={setDraftSubIBs}
            subIBsLoadFailed={subIBsLoadFailed}
            onRetrySubIBs={onRetrySubIBs}
          />
        )}

        {showDateField && (
          <FilterField
            label={translatedContent.filters?.dataTimeRangeLabel}
            hint={translatedContent.filters?.datesHint}
          >
            <Datepicker
              value={draftDateRange}
              inputId="dateRangeFilterModal"
              onChange={setDraftDateRange}
              displayVariant="field"
              popoverDirection="up"
              allowClear={false}
              configs={{
                shortcuts: {
                  today: translatedContent.today,
                  yesterday: translatedContent.yesterday,
                  past: (period) =>
                    translatedContent.past
                      ? translatedContent.past(period)
                      : "",
                  currentMonth: translatedContent.currentMonth,
                  pastMonth: translatedContent.pastMonth,
                },
                footer: {
                  cancel: translatedContent.cancel,
                  apply: translatedContent.apply,
                },
              }}
              i18n={language}
              minDate={new Date(CLIENT_TRADING_TABLE_FILTER_REPORT_START_DATE)}
              tooltip={
                translatedContent.filters
                  ?.tradingReportDatePickerDisclaimerTooltip
              }
              onTooltipHover={onDateTooltipHover}
            />
          </FilterField>
        )}
      </div>
    </ResponsiveModal>
  );
};
